import React, { useEffect, useRef, useState } from "react";
import styles from "./Billing.module.scss";
import SubscriptionDetails from "components/billing/SubscriptionDetails";
import Button from "components/shared/Button";
import PaymentForm, { PaymentFormHandle } from "../../../components/billing/PaymentForm/PaymentForm";
import Modal from "components/shared/Modal";
import TermsOfUse from "components/shared/TermsOfUse";
import Preloader from "components/shared/Preloader";
import { ArrowIcon } from 'assets/icons/ArrowIcon';
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { useAppDispatch } from "app/store";
import { customerInfoSelector, isLoadingCustomerInfoSelector } from "features/billing/customer/customerSlice";
import { isLoadingSubscriptionInfoSelector, subscriptionInfoSelector } from "features/billing/subscription/subscriptionSlice";
import {
  setPaymentFormAddress,
  setPaymentFormAddressErrors,
  setPaymentFormCardholderName,
  setPaymentFormCardholderNameError
} from "features/billing/formStates/paymentFormInfo/paymentFormInfoActions";
import { cancelSubscription, getSubscriptionInfoById } from "features/billing/subscription/subscriptionActions";
import { useStripe } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { IAddUpdateSubscriptionData, SubscriptionStatus } from "features/billing/subscription/subscriptionTypes";
import { payLatestInvoiceBySubscriptionId } from "features/billing/invoiceInfo/invoiceInfoActions";
import { InvoiceStatus } from "features/billing/invoices/invoicesTypes";
import { getCouponById } from "features/billing/couponInfo/couponInfoActions";
import { billingFormsSubscriptionInfoSelector } from "features/billing/formStates/billingFormsInfo/billingFormsInfoSlice";
import { setSubscriptionFormInfo } from "features/billing/formStates/billingFormsInfo/billingFormsInfoActions";
import { changeSubscriptionInfo } from "api/billing/subscriptions";

const Billing: React.FC = () => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const navigate = useNavigate();

  const customerInfo = useSelector(customerInfoSelector);
  const subscriptionInfo = useSelector(subscriptionInfoSelector);
  const customerIsLoading = useSelector(isLoadingCustomerInfoSelector);
  const subscriptionIsLoading = useSelector(isLoadingSubscriptionInfoSelector);
  const subscriptionFormInfo = useSelector(billingFormsSubscriptionInfoSelector);

  const [termsOfUseAccepted, setTermsOfUseAccepted] = useState(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] = useState(false);
  const [customerInfoIsVisible, setCustomerInfoIsVisible] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const btnW160 = React.useMemo(() => ({ width: 160 }), []);
  const btnMaxW170 = React.useMemo(() => ({ maxWidth: 170 }), []);

  const paymentFormRef = useRef<PaymentFormHandle>(null);

  useEffect(() => {
    if (!subscriptionInfo) return;

    dispatch(setSubscriptionFormInfo({
      ...subscriptionFormInfo,
      productId: subscriptionInfo!.productId,
      interval: subscriptionInfo!.interval,
      currency: subscriptionInfo!.currency,
      quantity: subscriptionInfo!.quantity,
    }));


    if (subscriptionInfo.couponId) {
      dispatch(getCouponById(subscriptionInfo.couponId))
    }
  }, [dispatch, subscriptionInfo]);

  const toggleVisibility = React.useCallback(() => {
    setCustomerInfoIsVisible(v => !v);
  }, []);

  const handleSubscriptionUpdate = async (quantity: number, priceId: string, couponId: string | null) => {
    const updateSubscriptionData: IAddUpdateSubscriptionData = {
      subscriptionId: subscriptionInfo!.id,
      priceId: priceId,
      quantity: quantity,
      couponId: couponId,
    }

    await changeSubscriptionInfo(updateSubscriptionData);

    const ok = await handleSubscriptionLatestInvoicePayment(
      () => dispatch(getSubscriptionInfoById(subscriptionInfo!.id))
    );
    return ok;
  }

  const handleSubscriptionPayment = async (paymentMethodId?: string): Promise<boolean> => {
    const ok = await handleSubscriptionLatestInvoicePayment(
      () => dispatch(getSubscriptionInfoById(subscriptionInfo!.id)),
      paymentMethodId
    );
    return ok;
  }

  const handleSubscriptionLatestInvoicePayment = async (
    updateSubscriptionStateFunc: () => void,
    paymentMethodId?: string
  ): Promise<boolean> => {
    let success = false;
    setIsProcessingPayment(true);

    try {
      paymentMethodId ??= customerInfo.defaultPaymentMethodId;

      const invoice = await dispatch(payLatestInvoiceBySubscriptionId(subscriptionInfo!.id)).unwrap();

      if (invoice.status === InvoiceStatus.Paid) {
        success = true;
        return success;
      }

      if (!stripe) {
        toast.error("Stripe not initialized");
        return false;
      }

      if (!invoice.confirmationClientSecret) {
        toast.error("Payment failed");
        return false;
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        invoice.confirmationClientSecret,
        { payment_method: paymentMethodId }
      );

      if (error || paymentIntent?.status !== "succeeded") {
        toast.error("Payment failed");
        return false;
      }

      success = true;
      return success;

    } catch (error) {
      toast.error("Payment failed");
      return false;

    } finally {
      updateSubscriptionStateFunc();
      setIsProcessingPayment(false);
    }
  };

  if (customerIsLoading || subscriptionIsLoading || !subscriptionInfo) {
    return <Preloader />
  }

  const subscriptionIsActive = subscriptionInfo.status === SubscriptionStatus.Active;
  const subscriptionIsTrialing = subscriptionInfo.status === SubscriptionStatus.Trialing;
  const subscriptionCanceled = subscriptionInfo.status === SubscriptionStatus.Canceled;
  const notActiveAndNotTrialing = !subscriptionIsActive && !subscriptionIsTrialing;

  return (
    <div className={styles.container}>
      <section className={styles.billingInfo}>
        {subscriptionIsTrialing && (
          <div className={styles.segment}>
            <p>
              <span className={styles.option}>Free trial ends on:</span>{" "}
              {format(new Date(subscriptionInfo!.trialEnd), 'MMM dd, yyyy')}
            </p>
          </div>
        )}

        {subscriptionCanceled && (
          <div className={`${styles.segment} ${styles.alignItemsCenter}`}>
            <section>
              <p>Your subscription was canceled because payments failed for a long time.</p>
              <p>To continue using the app, please choose a new plan.</p>
            </section>
            <Button
              variant="primary"
              onClick={() => navigate("/subscription/plans")}
              style={btnW160}
            >
              Choose a Plan
            </Button>
          </div>
        )}

        {!subscriptionCanceled && notActiveAndNotTrialing && (
          <div className={`${styles.segment} ${styles.alignItemsCenter}`}>
            <p>{`Your subscription payment couldn't be processed`}</p>

            <div className={styles.actionButtons}>
              <Button
                variant="primary"
                disabled={isProcessingPayment}
                preloader={isProcessingPayment}
                onClick={() => handleSubscriptionPayment()}
                style={btnW160}
              >
                Try Again
              </Button>

              <Button
                variant="primary"
                disabled={isProcessingPayment}
                onClick={() => setAddCardModalOpen(true)}
                aria-label="Add card and pay"
                style={btnW160}
              >
                Add Card and Pay
              </Button>
              {subscriptionInfo.periodEnded && (
                <Button
                  variant="primary"
                  disabled={isProcessingPayment}
                  onClick={() => navigate("/subscription/plans")}
                  style={btnW160}
                >
                  Select New Plan
                </Button>
              )}
            </div>
          </div>
        )}

        {!subscriptionCanceled && (
          <>
            <div className={styles.segment}>
              <div className={styles.descriptionItems}>
                <h2>Payment Information</h2>
                {customerInfo.paymentMethodType === null ? (
                  <p>Your payment information is not yet attached.</p>
                ) : customerInfo.paymentMethodType === "card" ? (
                  <div>
                    <p>Card ({customerInfo.cardBrand}): **** **** **** {customerInfo.cardLast4}</p>
                    <p>Expiration date: {customerInfo.cardExpMonth}/{customerInfo.cardExpYear}</p>
                  </div>
                ) : (
                  <p>Payment method type: {customerInfo.paymentMethodType}</p>
                )}
                {subscriptionIsTrialing && (
                  <p>Your payment details will be requested once your free trial concludes.</p>
                )}
                {notActiveAndNotTrialing && customerInfo.paymentMethodType && (
                  <p>Your subscription is unpaid. Please make a payment to continue the service.</p>
                )}
              </div>

              {subscriptionIsActive && (
                <Button
                  aria-label={customerInfo.paymentMethodType === null ? 'Add payment card' : 'Edit payment card'}
                  variant="primary"
                  onClick={() => setAddCardModalOpen(true)}
                  style={btnMaxW170}
                >
                  {customerInfo.paymentMethodType === null ? 'Add Payment Card' : 'Edit Payment Card'}
                </Button>
              )}
            </div>

            <div className={styles.segment}>
              <div className={styles.descriptionItems}>
                <h2 className={styles.cursorPointer} onClick={toggleVisibility}>
                  Customer Information
                  <div className={`${styles.menuArrow} ${customerInfoIsVisible ? styles.rotate : ''}`}>
                    <ArrowIcon />
                  </div>
                </h2>

                <p><span className={styles.option}>Invoices sent to:</span> {customerInfo.email}</p>

                {customerInfoIsVisible && (
                  <article className={`${styles.customerInformation} ${styles.flexColumn}`}>
                    <p><span className={styles.option}>Country:</span> {customerInfo.address?.country || "is not set"}</p>
                    <p><span className={styles.option}>City:</span> {customerInfo.address?.city || "is not set"}</p>
                    <p><span className={styles.option}>Address:</span> {customerInfo.address?.line1 || "is not set"}</p>
                    <p><span className={styles.option}>Address 2:</span> {customerInfo.address?.line2 || "is not set"}</p>
                    <p><span className={styles.option}>ZIP / Postal code:</span> {customerInfo.address?.postalCode || "is not set"}</p>
                  </article>
                )}
              </div>
            </div>
          </>
        )}

        {subscriptionIsActive && !subscriptionInfo.cancelAtPeriodEnd && (
          <div className={styles.segment}>
            <h2>Cancel Subscription</h2>
            <Button
              aria-label="Cancel"
              variant="default"
              style={btnMaxW170}
              onClick={() => setCancelSubscriptionModalOpen(true)}
            >
              Cancel
            </Button>
          </div>
        )}
      </section>

      {!notActiveAndNotTrialing && (
        <div className={styles.right} hidden={notActiveAndNotTrialing}>
          <SubscriptionDetails
            intervalSelectionEnabled={false}
            submitButtonHidden={false}
            handleSubmit={async (quantity, priceId, couponId) => {
              await handleSubscriptionUpdate(quantity, priceId, couponId);
            }}
          />
        </div>
      )}

      <Modal
        title={customerInfo.paymentMethodType === null ? 'Add card' : 'Edit card'}
        open={addCardModalOpen}
        onClose={() => {
          dispatch(setPaymentFormAddressErrors({
            country: '',
            state: '',
            line1: '',
            postalCode: '',
            city: ''
          }))
          dispatch(setPaymentFormAddress({
            country: '',
            state: '',
            line1: '',
            line2: '',
            postalCode: '',
            city: ''
          }))
          dispatch(setPaymentFormCardholderNameError(""))
          dispatch(setPaymentFormCardholderName(""))
          setAddCardModalOpen(false)
        }}
      >
        <div className={styles.modalContainer}>
          <PaymentForm ref={paymentFormRef} />

          <Button
            variant="primary"
            disabled={!termsOfUseAccepted || isProcessingPayment}
            onClick={() => paymentFormRef.current?.triggerSubmit().then(async (result) => {
              if (!result.success) {
                return;
              }

              if (notActiveAndNotTrialing && customerInfo.paymentMethodType) {
                const paid = await handleSubscriptionPayment(result.paymentMethodId);
                if (!paid) {
                  return;
                }
              }

              setAddCardModalOpen(false)
              setTermsOfUseAccepted(false)
            })}
            style={{ maxWidth: 160, marginLeft: 'auto' }}
          >
            {customerInfo.paymentMethodType === null ? 'Add card' : notActiveAndNotTrialing ? 'Confirm and pay' : 'Edit card'}
          </Button>

          <TermsOfUse
            additionalMessages={[
              'By providing your card information, you allow trackera.io to charge your card for future payments in accordance with their terms.',
            ]}
            termsAccepted={termsOfUseAccepted}
            setTermsAccepted={setTermsOfUseAccepted}
          />
        </div>
      </Modal>

      <Modal
        title='Cancel Subscription'
        open={cancelSubscriptionModalOpen}
        onClose={() => setCancelSubscriptionModalOpen(false)}
      >
        <div className={styles.modalContainer}>
          <p>Cancelling your subscription will take effect on <strong>{format(new Date(subscriptionInfo.periodEnd), 'MMM dd, yyyy')}</strong></p>
          <p>Until then, you will continue to enjoy all premium features</p>
          <p>After this date, you will lose access to premium content and related data</p>

          <div className={styles.actionButtons}>
            <Button
              aria-label='Cancel'
              variant="primary"
              style={btnMaxW170}
              onClick={() => setCancelSubscriptionModalOpen(false)}
            >
              Keep Subscription
            </Button>

            <Button
              aria-label='Cancel'
              variant="default"
              style={btnMaxW170}
              onClick={() => dispatch(cancelSubscription(subscriptionInfo.id))}
            >
              Cancel Subscription
            </Button>
          </div >
        </div>
      </Modal >
    </div>
  )
};

export default Billing;
